/* Reading a broker's own record of what you did.
 *
 * §1 rules out connecting to a brokerage: no passwords, no trading keys, no
 * OAuth. That rules out polling an account, and it does not rule out this — the
 * broker already writes down every fill and will hand you the file. Importing it
 * is the same act the spec describes, "record a sale I already executed", with
 * the broker doing the typing.
 *
 * Two things this deliberately does not do. It never touches holdings: a full
 * history imported on top of positions that already exist would count every
 * share twice, and only you know which of the two is the truth. And it never
 * guesses at a row it cannot read — an unreadable line is reported with its
 * number, not rounded into the book. */

import { toNumber } from '../lib/csv';
import type { Account, Currency, Holding, Trade, TradeColumns } from '../types';

/** Lives in types because a broker record remembers one. */
export type ColumnMap = TradeColumns;

export interface ImportedRow {
  line: number;
  trade: Trade;
  /** Already in the book, matched on the same broker, day, ticker, side, size
      and price. Re-importing the same file must not double anything. */
  duplicate: boolean;
}

export interface ImportResult {
  rows: ImportedRow[];
  skipped: Array<{ line: number; why: string }>;
  /** Which way round an ambiguous d/m/y file was read, when it mattered. */
  dateOrder: 'iso' | 'day-first' | 'month-first' | 'mixed';
}

/* ---------- header guessing ---------- */

const HINTS: Record<keyof ColumnMap, string[]> = {
  at: ['date', 'time', 'datetime', 'trade date', 'data', 'czas', 'when', 'settled'],
  ticker: ['symbol', 'ticker', 'instrument', 'asset', 'security', 'walor', 'nazwa'],
  side: ['side', 'action', 'type', 'buy/sell', 'direction', 'kierunek', 'operacja', 'typ'],
  shares: ['quantity', 'qty', 'shares', 'volume', 'units', 'ilosc', 'ilość', 'wolumen'],
  price: ['price', 'rate', 'unit price', 'cena', 'kurs', 'fill price'],
};

/** Best guess at which column is which, from the header row. A guess is a
    starting point for the mapping the user confirms, never a substitute for it:
    "Type" means the side at one broker and the instrument class at another. */
export function guessColumns(header: string[]): Partial<ColumnMap> {
  const cells = header.map((h) => h.trim().toLowerCase());
  const out: Partial<ColumnMap> = {};
  const taken = new Set<number>();

  for (const field of Object.keys(HINTS) as Array<keyof ColumnMap>) {
    let best = -1;
    let bestScore = 0;
    cells.forEach((cell, i) => {
      if (taken.has(i) || !cell) return;
      for (const hint of HINTS[field]) {
        // An exact header beats one that merely contains the word.
        const score = cell === hint ? 3 : cell.includes(hint) ? 2 : 0;
        if (score > bestScore) { bestScore = score; best = i; }
      }
    });
    if (best >= 0) { out[field] = best; taken.add(best); }
  }
  return out;
}

/* ---------- dates ---------- */

const ISO = /^(\d{4})-(\d{2})-(\d{2})/;
const SPLIT = /^(\d{1,2})[./-](\d{1,2})[./-](\d{4})/;

/** Which component is the day, decided across the whole file rather than row by
    row. A single date like 03/06/2026 cannot be read on its own; a file that
    also contains 19/06/2026 can, because no month is 19. */
function detectOrder(values: string[]): ImportResult['dateOrder'] {
  let sawSplit = false;
  let firstOver12 = false;
  let secondOver12 = false;
  for (const v of values) {
    const m = SPLIT.exec(v.trim());
    if (!m) continue;
    sawSplit = true;
    if (Number(m[1]) > 12) firstOver12 = true;
    if (Number(m[2]) > 12) secondOver12 = true;
  }
  if (!sawSplit) return 'iso';
  if (firstOver12 && secondOver12) return 'mixed';
  if (secondOver12) return 'month-first';
  // Day-first is the default: it is what every broker these accounts use writes,
  // and an unresolvable file is reported so the reading can be checked.
  return 'day-first';
}

function toIsoDate(raw: string, order: ImportResult['dateOrder']): string | null {
  const v = raw.trim();
  const iso = ISO.exec(v);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

  const m = SPLIT.exec(v);
  if (!m) return null;
  const a = Number(m[1]);
  const b = Number(m[2]);
  const year = m[3]!;
  const day = order === 'month-first' ? b : a;
  const month = order === 'month-first' ? a : b;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/* ---------- sides ---------- */

const BUY = ['buy', 'bought', 'b', 'kupno', 'k', 'purchase', 'long'];
const SELL = ['sell', 'sold', 's', 'sprzedaz', 'sprzedaż', 'sale', 'short'];

function toSide(raw: string, shares: number): Trade['side'] | null {
  const v = raw.trim().toLowerCase();
  if (BUY.some((w) => v === w || v.startsWith(`${w} `))) return 'buy';
  if (SELL.some((w) => v === w || v.startsWith(`${w} `))) return 'sell';
  // Some exports carry no side column at all and sign the quantity instead.
  if (!v && shares !== 0) return shares < 0 ? 'sell' : 'buy';
  return null;
}

/* ---------- the import ---------- */

/** Stable across re-imports: the same fill read twice produces the same id, so
    running a file again updates rather than duplicates. */
export function tradeId(t: Pick<Trade, 'account' | 'ticker' | 'side' | 'shares' | 'price' | 'at'>): string {
  const day = t.at.slice(0, 10);
  const size = t.shares.toFixed(6);
  const price = t.price.toFixed(6);
  return `imp-${t.account}-${day}-${t.ticker}-${t.side}-${size}-${price}`;
}

export function readTrades(
  rows: string[][],
  map: ColumnMap,
  account: Account,
  existing: Trade[],
): ImportResult {
  const known = new Set(existing.map((t) => t.id));
  const body = rows.slice(1);
  const dateOrder = detectOrder(body.map((r) => r[map.at] ?? ''));

  const out: ImportedRow[] = [];
  const skipped: Array<{ line: number; why: string }> = [];

  body.forEach((row, i) => {
    const line = i + 2;                      // 1-based, and the header is line 1
    const at = toIsoDate(row[map.at] ?? '', dateOrder);
    if (!at) return skipped.push({ line, why: `no date in "${(row[map.at] ?? '').trim()}"` });

    const ticker = (row[map.ticker] ?? '').trim().toUpperCase();
    if (!ticker) return skipped.push({ line, why: 'no ticker' });

    const rawShares = toNumber(row[map.shares] ?? '');
    if (rawShares === null || rawShares === 0) {
      return skipped.push({ line, why: `no share count in "${(row[map.shares] ?? '').trim()}"` });
    }

    const side = toSide(row[map.side] ?? '', rawShares);
    if (!side) return skipped.push({ line, why: `cannot tell buy from sell in "${(row[map.side] ?? '').trim()}"` });

    const price = toNumber(row[map.price] ?? '');
    if (price === null || price <= 0) {
      return skipped.push({ line, why: `no price in "${(row[map.price] ?? '').trim()}"` });
    }

    const trade: Trade = {
      id: '',
      ticker,
      account,
      side,
      shares: Math.abs(rawShares),
      price,
      at: `${at}T12:00:00.000Z`,
    };
    trade.id = tradeId(trade);
    out.push({ line, trade, duplicate: known.has(trade.id) });
  });

  return { rows: out, skipped, dateOrder };
}

/* ---------- applying the trades to the book ---------- */

/** Fractional shares are the point here, so "sold out" is a tolerance rather
    than a zero: 208.6132 bought and 208.6132 sold must close the position. */
const EPSILON = 1e-9;

export interface Resolved {
  yahooSymbol: string;
  currency: Currency;
  name?: string;
}

export interface ApplyResult {
  /** Holdings to write: created or changed, and still holding something. */
  put: Holding[];
  /** Holdings the run emptied. */
  removeIds: string[];
  /** Every touched holding exactly as it stood before — what an undo restores.
      Together with `createdIds` this reverses the run completely, without ever
      subtracting arithmetic that something else may have changed since. */
  before: Holding[];
  createdIds: string[];
  /** The same trades, with the realised gain filled in on each sell. */
  trades: Trade[];
  problems: Array<{ ticker: string; why: string }>;
}

export function applyTrades(
  input: Trade[],
  holdings: Holding[],
  account: Account,
  options: {
    resolve: (ticker: string) => Resolved | null;
    makeId: (ticker: string) => string;
    fallbackCurrency: Currency;
  },
): ApplyResult {
  const existing = new Map<string, Holding>();
  for (const h of holdings) if (h.account === account) existing.set(h.ticker, h);

  const before = new Map<string, Holding>();
  const working = new Map<string, Holding>();
  const createdIds: string[] = [];
  const problems: ApplyResult['problems'] = [];
  const trades: Trade[] = [];

  // Oldest first: an average cost depends on the order the lots arrived in.
  const ordered = [...input].sort((a, b) => a.at.localeCompare(b.at));

  for (const t of ordered) {
    const key = t.ticker;
    if (!working.has(key)) {
      const found = existing.get(key);
      if (found) {
        before.set(key, { ...found });
        working.set(key, { ...found });
      }
    }
    const held = working.get(key);

    if (t.side === 'buy') {
      if (!held) {
        const resolved = options.resolve(key);
        const created: Holding = {
          id: options.makeId(key),
          ticker: key,
          // §3.2: the exact fetch symbol, never guessed later. When the upstream
          // did not answer, the typed ticker stands in and the position simply
          // shows as unpriced — which is visible, unlike a wrong symbol.
          yahooSymbol: resolved?.yahooSymbol ?? key,
          account,
          shares: t.shares,
          avgCost: t.price,
          currency: resolved?.currency ?? options.fallbackCurrency,
          openedAt: t.at.slice(0, 10),
        };
        if (resolved?.name) created.name = resolved.name;
        working.set(key, created);
        createdIds.push(created.id);
      } else {
        const cost = held.shares * held.avgCost + t.shares * t.price;
        held.shares += t.shares;
        held.avgCost = held.shares > EPSILON ? cost / held.shares : t.price;
      }
      trades.push(t);
      continue;
    }

    // A sell.
    if (!held || held.shares <= EPSILON) {
      problems.push({ ticker: key, why: 'sold on a date the book holds none of it' });
      continue;
    }
    let size = t.shares;
    if (size > held.shares + EPSILON) {
      problems.push({
        ticker: key,
        why: `sold ${size} but only ${held.shares} was held, recorded the ${held.shares}`,
      });
      size = held.shares;
    }
    // Average cost survives a sale; only the count moves (§5.1).
    const sold: Trade = { ...t, shares: size, realizedGain: (t.price - held.avgCost) * size };
    held.shares -= size;
    trades.push(sold);
  }

  const put: Holding[] = [];
  const removeIds: string[] = [];
  for (const h of working.values()) {
    if (h.shares <= EPSILON) {
      // Only an existing holding can be removed; one this run created and then
      // closed simply never lands.
      if (!createdIds.includes(h.id)) removeIds.push(h.id);
    } else put.push(h);
  }

  return { put, removeIds, before: [...before.values()], createdIds, trades, problems };
}
